import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { KpiStrip, KpiTileLoading, type KpiItem } from '../components/KpiTile';
import { EmptyState } from '../components/EmptyState';
import { SkeletonRow } from '../components/SkeletonRow';
import { AlertTypePill, SignalChip } from '../components/AlertTypePill';
import { HoursBar } from '../components/HoursBar';
import {
  generateEngagementDigestPreview,
  getEngagementHealth,
  getEngagementDigestConfig,
  getEngagementDigestRun,
  getEngagementDigestRuns,
  refreshEngagementHealth,
  sendEngagementDigestRun,
  snoozeAlert,
  unsnoozeAlert,
} from '../lib/api';
import { fmt } from '../lib/format';
import { buildAlertMailto } from '../lib/affiliate-mailto';
import { downloadRiskRankingWorkbook } from '../lib/engagement-health-export';
import {
  BLANK_ENGAGEMENT_STATUS,
  DEFAULT_ENGAGEMENT_STATUSES,
  ALL_ACCOUNTS,
  accountIsSelected,
  accountNameForDisplay,
  engagementStatusValue,
  filterEngagementAssessments,
  formatDigestNextRun,
  formatDigestRunLabel,
  formatDigestSchedule,
  isDefaultStatuses,
  readHealthUrlState,
  selectedAccountCount,
  sortNearCompletion,
  summarizeActiveEngagements,
  toggleAccountSelection,
  writeHealthUrlState,
  type AccountSelection,
  type EngagementHealthView,
} from '../lib/engagement-health';
import type {
  AlertRow,
  AlertType,
  EngagementAssessment,
  EngagementDigestMessage,
  RiskTier,
} from '../types';

const SF_INSTANCE_URL = import.meta.env.VITE_SF_INSTANCE_URL ?? '';

const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  stalled_start: 'Stalled starts',
  near_completion: 'Near completion',
  stale_activity: 'SOW ending / overdue',
  stale_activity_30d: 'No session 30d',
};

const ALERT_TYPES: AlertType[] = ['stalled_start', 'near_completion', 'stale_activity', 'stale_activity_30d'];

const RISK_TIER_LABELS: Record<RiskTier, string> = {
  not_scored: 'Not scored',
  healthy: 'Healthy',
  watch: 'Watch',
  at_risk: 'At risk',
  critical: 'Critical',
};

const RISK_TIERS: RiskTier[] = ['critical', 'at_risk', 'watch', 'healthy', 'not_scored'];
const KNOWN_ENGAGEMENT_STATUSES = [
  'Active',
  'On Hold',
  'Completed',
  'Cancelled',
  'Not Applicable',
  BLANK_ENGAGEMENT_STATUS,
];
interface EngagementHealthFilters {
  accounts: AccountSelection;
  affiliate: string[];
  owners: string[];
  csms: string[];
  responsible: string[];
  signalTypes: AlertType[];
  statuses: string[];
  riskTiers: RiskTier[];
  opportunity: string;
  sowFrom: string;
  sowTo: string;
  showSnoozed: boolean;
}

const DEFAULT_HEALTH_FILTERS: EngagementHealthFilters = {
  accounts: ALL_ACCOUNTS,
  affiliate: [],
  owners: [],
  csms: [],
  responsible: [],
  signalTypes: [],
  statuses: [...DEFAULT_ENGAGEMENT_STATUSES],
  riskTiers: [],
  opportunity: '',
  sowFrom: '',
  sowTo: '',
  showSnoozed: false,
};

export function EngagementHealth(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['engagement-health'],
    queryFn: getEngagementHealth,
  });

  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState<EngagementHealthFilters>(() =>
    readHealthUrlState(params).filters,
  );
  const [collapsed, setCollapsed] = useState(false);
  const [view, setView] = useState<EngagementHealthView>(() => readHealthUrlState(params).view);

  const refresh = useMutation({
    mutationFn: refreshEngagementHealth,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagement-health'] }),
  });

  const snooze = useMutation({
    mutationFn: ({ id, type }: { id: string; type: AlertType }) => snoozeAlert(id, type),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagement-health'] }),
  });
  const unsnooze = useMutation({
    mutationFn: ({ id, type }: { id: string; type: AlertType }) => unsnoozeAlert(id, type),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagement-health'] }),
  });

  const alerts = data?.alerts ?? [];
  const engagements = data?.engagements ?? [];
  const snapshot = data?.snapshot;
  const accountUniverseCount = useMemo(
    () => new Set(engagements.map(accountNameForDisplay).filter((name): name is string => !!name)).size,
    [engagements],
  );

  useEffect(() => {
    const state = readHealthUrlState(params);
    setFilters(state.filters);
    setView(state.view);
  }, [params]);

  const updateFilters = (next: EngagementHealthFilters) => {
    setFilters(next);
    setParams(writeHealthUrlState(view, next), { replace: true });
  };

  const updateView = (next: EngagementHealthView) => {
    setView(next);
    setParams(writeHealthUrlState(next, filters), { replace: true });
  };

  // A true clear (statuses: [] = all statuses), so it can never be a no-op
  // when the default status filter itself is what hides every row. The
  // sidebar's "Reset all filters" returns to DEFAULT_HEALTH_FILTERS instead.
  const clearFilters = () => {
    const cleared = { ...DEFAULT_HEALTH_FILTERS, accounts: ALL_ACCOUNTS, statuses: [] };
    setFilters(cleared);
    setParams(writeHealthUrlState(view, cleared), { replace: true });
  };

  const filteredEngagements = useMemo(() => filterEngagementAssessments(engagements, {
    accounts: filters.accounts,
    affiliate: filters.affiliate,
    owners: filters.owners,
    csms: filters.csms,
    responsible: filters.responsible,
    signalTypes: filters.signalTypes,
    statuses: filters.statuses,
    riskTiers: filters.riskTiers,
    opportunity: filters.opportunity,
    sowFrom: filters.sowFrom,
    sowTo: filters.sowTo,
  }), [engagements, filters]);

  const exportRiskRanking = useMutation({
    mutationFn: () => downloadRiskRankingWorkbook(filteredEngagements),
  });

  const activeSummary = useMemo(
    () => summarizeActiveEngagements(filteredEngagements),
    [filteredEngagements],
  );

  const nearCompletionEngagements = useMemo(
    () => sortNearCompletion(filteredEngagements.filter((engagement) =>
      engagement.riskSignals.some((signal) => signal.type === 'near_completion'),
    )),
    [filteredEngagements],
  );

  const qualifyingOpportunityIds = useMemo(
    () => new Set(filteredEngagements.map((engagement) => engagement.opportunityId)),
    [filteredEngagements],
  );

  const filteredAlerts = useMemo(() => {
    const now = Date.now();
    return alerts.filter((a) => {
      // Legacy snapshots (pre-assessment) have no engagement rows; skip the
      // membership check then, or every alert would be hidden.
      if (engagements.length > 0 && !qualifyingOpportunityIds.has(a.opportunityId)) return false;
      if (filters.signalTypes.length > 0 && !filters.signalTypes.includes(a.alertType)) return false;
      const snoozed = a.snooze && Date.parse(a.snooze.snoozedUntil) > now;
      if (snoozed && !filters.showSnoozed) return false;
      return true;
    });
  }, [alerts, engagements.length, filters.showSnoozed, filters.signalTypes, qualifyingOpportunityIds]);

  const hasFilters = countHealthFilters(filters) > 0;

  const kpis: KpiItem[] = snapshot && !data?.requiresRefresh
    ? [
        {
          label: 'Stalled starts',
          value: activeSummary.counts.stalledStart,
          sub: 'no hours logged > 2 weeks past start',
        },
        {
          label: 'Near completion',
          value: activeSummary.counts.nearCompletion,
          sub: '≥90% of contracted hours used',
        },
        {
          label: 'Stale activity',
          value: activeSummary.counts.staleActivity,
          sub: 'SOW ending, still open',
        },
        {
          label: 'No session 30d',
          value: activeSummary.counts.staleActivity30d,
          sub: 'started, no session in 30+ days',
        },
        {
          label: 'Active engagements',
          value: activeSummary.engagements,
          sub: snapshot.generatedAt ? `Last refresh ${fmt.dateTime(snapshot.generatedAt)}` : '',
        },
      ]
    : [];

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <div className="page-hd">
          <div>
            <div className="eb">Engagement health</div>
            <h1 className="d-title">Rank and investigate coaching engagements</h1>
            <div className="d-meta" style={{ marginTop: 8, fontSize: 13 }}>
              {snapshot
                ? <>
                    Last refresh <span className="d-tnum">{fmt.dateTime(snapshot.generatedAt)}</span>
                    {data?.requiresRefresh
                      ? ' · Active totals require refresh'
                      : <> · scanned {activeSummary.engagements} active engagements</>}
                  </>
                : 'No snapshot yet — refresh to populate.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              variant="primary"
              icon="refresh"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              {refresh.isPending ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="kpi-strip">{[0, 1, 2, 3].map((i) => <KpiTileLoading key={i} />)}</div>
        ) : kpis.length ? (
          <KpiStrip items={kpis} />
        ) : null}

        {data?.requiresRefresh && (
          <div
            className="d-meta"
            style={{ margin: '4px 0 12px', padding: '10px 14px', background: 'rgba(184,67,10,0.08)', border: '1px solid rgba(184,67,10,0.25)', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}
          >
            <Icon name="alert" size={14} style={{ color: 'var(--warn)' }} />
            <span style={{ flex: 1 }}>This snapshot predates risk assessments. Refresh once to build the ranking.</span>
            <Button size="sm" variant="secondary" icon="refresh" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
              {refresh.isPending ? 'Refreshing…' : 'Refresh now'}
            </Button>
          </div>
        )}

        <div className="page-body" style={{ flexDirection: 'row' }}>
          {view !== 'digests' && (
            <EngagementHealthFilterSidebar
              engagements={engagements}
              filters={filters}
              setFilters={updateFilters}
              collapsed={collapsed}
              onToggle={() => setCollapsed(!collapsed)}
              showSnoozedControl={view === 'alerts'}
            />
          )}

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div className="toolbar">
              <div className="left">
                <Button size="sm" variant={view === 'risk' ? 'primary' : 'ghost'} onClick={() => updateView('risk')}>
                  Risk ranking
                </Button>
                <Button size="sm" variant={view === 'near_completion' ? 'primary' : 'ghost'} onClick={() => updateView('near_completion')}>
                  Near completion
                </Button>
                <Button size="sm" variant={view === 'alerts' ? 'primary' : 'ghost'} onClick={() => updateView('alerts')}>
                  Alert details
                </Button>
                <Button size="sm" variant={view === 'digests' ? 'primary' : 'ghost'} onClick={() => updateView('digests')}>
                  Digest runs
                </Button>
                <span style={{ width: 1, height: 20, background: 'var(--border-1)', margin: '0 4px' }} />
                {view !== 'digests' && (hasFilters ? (
                  <EngagementHealthFilterChips
                    filters={filters}
                    setFilters={updateFilters}
                    onClear={clearFilters}
                    accountCount={accountUniverseCount}
                  />
                ) : (
                  <>
                    <Icon name="search" size={14} style={{ color: 'var(--mute)' }} />
                    <input
                      className="input"
                      placeholder="Search engagements…"
                      style={{ width: 240, border: '1px solid var(--border-1)' }}
                      value={filters.opportunity}
                      onChange={(e) => updateFilters({ ...filters, opportunity: e.target.value })}
                    />
                  </>
                ))}
              </div>
              <div className="right">
                {view === 'risk' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    icon="download"
                    onClick={() => exportRiskRanking.mutate()}
                    disabled={filteredEngagements.length === 0 || exportRiskRanking.isPending}
                    title="Export the currently filtered risk ranking"
                  >
                    {exportRiskRanking.isPending ? 'Exporting…' : 'Export .xlsx'}
                  </Button>
                )}
                {view === 'risk' && exportRiskRanking.isError && (
                  <span className="d-meta" style={{ color: 'var(--risk)' }}>Export failed</span>
                )}
                {view !== 'digests' && (
                  <span className="count">
                    <strong>{view === 'risk' ? filteredEngagements.length : view === 'near_completion' ? nearCompletionEngagements.length : filteredAlerts.length}</strong> of{' '}
                    {view === 'alerts' ? alerts.length : engagements.length} {view === 'alerts' ? 'alerts' : 'engagements'}
                    {hasFilters && <span> · filtered</span>}
                  </span>
                )}
              </div>
            </div>

            <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
              {view === 'digests' ? (
                <DigestRunsPanel />
              ) : isLoading ? (
                <table className="tbl gen health-table">
                  <thead><tr><th>Risk</th><th>Signals</th><th>Reporting account</th><th>Engagement / project</th><th>Owner / CSM</th><th>Status / affiliate</th><th>Utilization</th><th>SOW / activity</th><th /></tr></thead>
                  <tbody>{Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cells={9} />)}</tbody>
                </table>
              ) : view === 'risk' && data?.requiresRefresh ? (
                <EmptyState
                  icon="refresh"
                  title="Refresh required for risk ranking"
                  body="The latest alert snapshot is still available in the Alert details view, but it does not contain one-row-per-engagement assessments."
                  cta={<Button variant="primary" icon="refresh" onClick={() => refresh.mutate()}>Refresh now</Button>}
                />
              ) : view === 'risk' && filteredEngagements.length === 0 ? (
                <EmptyState
                  icon="check"
                  title={engagements.length === 0 ? 'No assessments — refresh to scan' : 'Nothing matches these filters'}
                  body={engagements.length === 0
                    ? 'Click Refresh to query Salesforce and compute the latest risk ranking.'
                    : 'Try clearing filters or widening the SOW start date range.'}
                  cta={engagements.length === 0
                    ? <Button variant="primary" icon="refresh" onClick={() => refresh.mutate()}>Refresh now</Button>
                    : <Button variant="secondary" onClick={clearFilters}>Clear filters</Button>}
                />
              ) : view === 'risk' ? (
                <RiskRankingTable engagements={filteredEngagements} />
              ) : view === 'near_completion' && nearCompletionEngagements.length === 0 ? (
                <EmptyState
                  icon="check"
                  title="No near-completion engagements match"
                  body="This view uses the current 90% utilization rule and excludes Executive Partner engagements."
                  cta={<Button variant="secondary" onClick={clearFilters}>Clear filters</Button>}
                />
              ) : view === 'near_completion' ? (
                <RiskRankingTable engagements={nearCompletionEngagements} />
              ) : filteredAlerts.length === 0 ? (
                <EmptyState
                  icon="check"
                  title={alerts.length === 0 ? 'No alerts in this snapshot' : 'Nothing matches these filters'}
                  body={alerts.length === 0
                    ? 'The scanned engagements did not trigger any health signals.'
                    : 'Try clearing filters or showing snoozed alerts.'}
                  cta={<Button variant="secondary" onClick={clearFilters}>Reset filters</Button>}
                />
              ) : (
                <table className="tbl gen health-table">
                  <thead>
                    <tr>
                      <th>Alert</th>
                      <th>Reporting account</th>
                      <th>Engagement / project</th>
                      <th>Owner / CSM</th>
                      <th>Status / affiliate</th>
                      <th>Utilization</th>
                      <th>SOW / activity</th>
                      <th style={{ width: 1 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAlerts.map((a) => (
                      <AlertRowView
                        key={a.id}
                        a={a}
                        onSnooze={() => snooze.mutate({ id: a.opportunityId, type: a.alertType })}
                        onUnsnooze={() => unsnooze.mutate({ id: a.opportunityId, type: a.alertType })}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface SidebarProps {
  engagements: EngagementAssessment[];
  filters: EngagementHealthFilters;
  setFilters(filters: EngagementHealthFilters): void;
  collapsed: boolean;
  onToggle(): void;
  showSnoozedControl: boolean;
}

function EngagementHealthFilterSidebar({
  engagements,
  filters,
  setFilters,
  collapsed,
  onToggle,
  showSnoozedControl,
}: SidebarProps): JSX.Element {
  const [accountSearch, setAccountSearch] = useState('');
  const [affiliateSearch, setAffiliateSearch] = useState('');
  const accounts = useMemo(
    () => [...new Set(engagements.map(accountNameForDisplay).filter((x): x is string => !!x))].sort(),
    [engagements],
  );
  const affiliates = useMemo(
    () => [...new Set(engagements.flatMap((a) => a.deliveringAffiliates?.map((d) => d.name) ?? []).filter((x): x is string => !!x))].sort(),
    [engagements],
  );
  const owners = useMemo(() => uniquePeople(engagements.map((engagement) => ({
    key: engagement.ownerId ?? engagement.ownerName,
    name: engagement.ownerName,
  }))), [engagements]);
  const csms = useMemo(() => uniquePeople(engagements.map((engagement) => ({
    key: engagement.csmId ?? engagement.csmName,
    name: engagement.csmName,
  }))), [engagements]);
  const shownAccounts = accounts.filter((account) => account.toLowerCase().includes(accountSearch.trim().toLowerCase()));
  const shownAffiliates = affiliates.filter((affiliate) => affiliate.toLowerCase().includes(affiliateSearch.trim().toLowerCase()));
  const statuses = useMemo(
    () => [...new Set([
      ...KNOWN_ENGAGEMENT_STATUSES,
      ...engagements.map((engagement) => engagementStatusValue(engagement.engagementStatus)),
    ])],
    [engagements],
  );
  const totalFilters = countHealthFilters(filters);

  const toggleStringArray = (key: 'affiliate' | 'statuses' | 'owners' | 'csms', value: string) => {
    const current = filters[key];
    const next = current.includes(value)
      ? current.filter((x) => x !== value)
      : [...current, value];
    setFilters({ ...filters, [key]: next });
  };

  const toggleSignal = (value: AlertType) => {
    const next = filters.signalTypes.includes(value)
      ? filters.signalTypes.filter((type) => type !== value)
      : [...filters.signalTypes, value];
    setFilters({ ...filters, signalTypes: next });
  };

  const toggleTier = (value: RiskTier) => {
    const next = filters.riskTiers.includes(value)
      ? filters.riskTiers.filter((tier) => tier !== value)
      : [...filters.riskTiers, value];
    setFilters({ ...filters, riskTiers: next });
  };

  return (
    <div className={'fsidebar' + (collapsed ? ' collapsed' : '')}>
      <div className="fsidebar-hd">
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="lbl">Filters</span>
            {totalFilters > 0 && (
              <span style={{ background: 'var(--accent)', color: '#fff', fontSize: 10, padding: '1px 6px', borderRadius: 99, fontWeight: 600, letterSpacing: 0.05 }}>
                {totalFilters}
              </span>
            )}
          </div>
        )}
        <div className="collapse-btn" onClick={onToggle} title={collapsed ? 'Expand filters' : 'Collapse filters'}>
          <Icon name={collapsed ? 'sidebarCollapsed' : 'sidebar'} size={15} />
        </div>
      </div>
      <div className="fsidebar-body">
        <div className="fs-group">
          <label>Status · <span style={{ color: 'var(--mute)' }}>{filters.statuses.length}/{statuses.length}</span></label>
          <div className="chk-list" style={{ maxHeight: 'none' }}>
            {statuses.map((status) => (
              <label key={status}>
                <input
                  type="checkbox"
                  checked={filters.statuses.includes(status)}
                  onChange={() => toggleStringArray('statuses', status)}
                />
                <span>{status}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>Risk tier</label>
          <div className="chk-list" style={{ maxHeight: 'none' }}>
            {RISK_TIERS.map((tier) => (
              <label key={tier}>
                <input type="checkbox" checked={filters.riskTiers.includes(tier)} onChange={() => toggleTier(tier)} />
                <span>{RISK_TIER_LABELS[tier]}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>Risk signals · match all</label>
          <div className="chk-list" style={{ maxHeight: 'none' }}>
            {ALERT_TYPES.map((type) => (
              <label key={type}>
                <input type="checkbox" checked={filters.signalTypes.includes(type)} onChange={() => toggleSignal(type)} />
                <span>{ALERT_TYPE_LABELS[type]}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>SOW start date</label>
          <div className="fs-row">
            <input
              className="input"
              type="date"
              aria-label="SOW start from"
              value={filters.sowFrom}
              onChange={(e) => setFilters({ ...filters, sowFrom: e.target.value })}
            />
            <input
              className="input"
              type="date"
              aria-label="SOW start to"
              value={filters.sowTo}
              onChange={(e) => setFilters({ ...filters, sowTo: e.target.value })}
            />
          </div>
        </div>
        <div className="fs-group">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <label>Account · <span style={{ color: 'var(--mute)' }}>{selectedAccountCount(filters.accounts, accounts.length)}/{accounts.length}</span></label>
            <span style={{ display: 'flex', gap: 7 }}>
              <button className="btn-link" type="button" onClick={() => setFilters({ ...filters, accounts: ALL_ACCOUNTS })}>Select all</button>
              <button className="btn-link" type="button" onClick={() => setFilters({ ...filters, accounts: { mode: 'include', values: [] } })}>Clear all</button>
            </span>
          </div>
          <input
            className="input"
            placeholder={`Search ${accounts.length} accounts…`}
            style={{ width: '100%', marginBottom: 6 }}
            value={accountSearch}
            onChange={(event) => setAccountSearch(event.target.value)}
          />
          <div className="chk-list">
            {shownAccounts.map((a) => (
              <label key={a} title={a}>
                <input
                  type="checkbox"
                  checked={accountIsSelected(filters.accounts, a)}
                  onChange={(event) => setFilters({
                    ...filters,
                    accounts: toggleAccountSelection(filters.accounts, a, event.target.checked),
                  })}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>Affiliate · <span style={{ color: 'var(--mute)' }}>{filters.affiliate.length || 0}/{affiliates.length}</span></label>
          <input
            className="input"
            placeholder={`Search ${affiliates.length} affiliates…`}
            style={{ width: '100%', marginBottom: 6 }}
            value={affiliateSearch}
            onChange={(event) => setAffiliateSearch(event.target.value)}
          />
          <div className="chk-list">
            {shownAffiliates.map((a) => (
              <label key={a} title={a}>
                <input type="checkbox" checked={filters.affiliate.includes(a)} onChange={() => toggleStringArray('affiliate', a)} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>Opportunity Owner · <span style={{ color: 'var(--mute)' }}>{filters.owners.length || 0}/{owners.length}</span></label>
          <div className="chk-list">
            {owners.map((owner) => (
              <label key={owner.key} title={owner.name}>
                <input type="checkbox" checked={filters.owners.includes(owner.key)} onChange={() => toggleStringArray('owners', owner.key)} />
                <span>{owner.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>CSM · <span style={{ color: 'var(--mute)' }}>{filters.csms.length || 0}/{csms.length}</span></label>
          <div className="chk-list">
            {csms.map((csm) => (
              <label key={csm.key} title={csm.name}>
                <input type="checkbox" checked={filters.csms.includes(csm.key)} onChange={() => toggleStringArray('csms', csm.key)} />
                <span>{csm.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="fs-group">
          <label>Opportunity</label>
          <input
            className="input"
            placeholder="Search name or Salesforce ID…"
            style={{ width: '100%' }}
            value={filters.opportunity}
            onChange={(e) => setFilters({ ...filters, opportunity: e.target.value })}
          />
        </div>
        {showSnoozedControl && (
          <div className="fs-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={filters.showSnoozed}
                onChange={(e) => setFilters({ ...filters, showSnoozed: e.target.checked })}
              />
              <span>Show snoozed alerts</span>
            </label>
          </div>
        )}
      </div>
      <div className="fsidebar-foot">
        <a className="btn-link" onClick={() => setFilters({ ...DEFAULT_HEALTH_FILTERS, accounts: ALL_ACCOUNTS })}>Reset all filters</a>
      </div>
    </div>
  );
}

interface Chip {
  key: keyof EngagementHealthFilters;
  label: string;
  val: string;
  display?: string;
}

interface ChipProps {
  filters: EngagementHealthFilters;
  setFilters(filters: EngagementHealthFilters): void;
  onClear(): void;
  accountCount: number;
}

function EngagementHealthFilterChips({ filters, setFilters, onClear, accountCount }: ChipProps): JSX.Element | null {
  const chips: Chip[] = [];
  if (filters.accounts.mode === 'exclude') {
    chips.push({ key: 'accounts', label: 'Excluded', val: filters.accounts.values.join(', ') || 'None' });
  } else if (filters.accounts.mode === 'include') {
    chips.push({
      key: 'accounts',
      label: 'Accounts',
      val: `${filters.accounts.values.length} of ${accountCount}`,
    });
  }
  filters.affiliate.forEach((v) => chips.push({ key: 'affiliate', label: 'Affiliate', val: v }));
  filters.owners.forEach((v) => chips.push({ key: 'owners', label: 'Owner', val: v }));
  filters.csms.forEach((v) => chips.push({ key: 'csms', label: 'CSM', val: v }));
  filters.responsible.forEach((v) => chips.push({ key: 'responsible', label: 'Responsible', val: v }));
  if (!isDefaultStatuses(filters.statuses)) {
    filters.statuses.forEach((v) => chips.push({ key: 'statuses', label: 'Status', val: v }));
  }
  filters.riskTiers.forEach((v) => chips.push({ key: 'riskTiers', label: 'Risk', val: v, display: RISK_TIER_LABELS[v] }));
  filters.signalTypes.forEach((v) => chips.push({ key: 'signalTypes', label: 'Signal (AND)', val: v, display: ALERT_TYPE_LABELS[v] }));
  if (filters.opportunity) chips.push({ key: 'opportunity', label: 'Opportunity', val: filters.opportunity });
  if (filters.sowFrom) chips.push({ key: 'sowFrom', label: 'SOW from', val: filters.sowFrom });
  if (filters.sowTo) chips.push({ key: 'sowTo', label: 'SOW to', val: filters.sowTo });
  if (filters.showSnoozed) chips.push({ key: 'showSnoozed', label: 'Snoozed', val: 'shown' });

  if (chips.length === 0) return null;

  const remove = (chip: Chip) => {
    if (chip.key === 'accounts') {
      setFilters({ ...filters, accounts: ALL_ACCOUNTS });
      return;
    }
    if (chip.key === 'showSnoozed') {
      setFilters({ ...filters, showSnoozed: false });
      return;
    }
    if (chip.key === 'signalTypes') {
      setFilters({ ...filters, signalTypes: filters.signalTypes.filter((v) => v !== chip.val) });
      return;
    }
    if (chip.key === 'riskTiers') {
      setFilters({ ...filters, riskTiers: filters.riskTiers.filter((v) => v !== chip.val) });
      return;
    }
    if (chip.key === 'statuses') {
      setFilters({ ...filters, statuses: filters.statuses.filter((v) => v !== chip.val) });
      return;
    }
    const current = filters[chip.key];
    if (Array.isArray(current)) {
      setFilters({ ...filters, [chip.key]: current.filter((v) => v !== chip.val) });
    } else {
      setFilters({ ...filters, [chip.key]: '' });
    }
  };

  return (
    <div className="fchips">
      <span className="d-meta" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600 }}>Filters</span>
      {chips.map((chip, i) => (
        <span className="fchip" key={`${chip.key}-${chip.val}-${i}`}>
          <span style={{ color: 'var(--mute)' }}>{chip.label}:</span> <strong>{chip.display ?? chip.val}</strong>
          <span className="x" onClick={() => remove(chip)}><Icon name="x" size={12} /></span>
        </span>
      ))}
      <a className="btn-link" style={{ marginLeft: 4, fontSize: 12 }} onClick={onClear}>Reset</a>
    </div>
  );
}

function countHealthFilters(filters: EngagementHealthFilters): number {
  return (filters.accounts.mode === 'all' ? 0 : 1)
    + filters.affiliate.length
    + filters.owners.length
    + filters.csms.length
    + filters.responsible.length
    + filters.signalTypes.length
    // The default status selection is the baseline view, not a user filter;
    // [] means "all statuses", which is also unfiltered.
    + (isDefaultStatuses(filters.statuses) ? 0 : filters.statuses.length)
    + filters.riskTiers.length
    + (filters.opportunity ? 1 : 0)
    + (filters.sowFrom ? 1 : 0)
    + (filters.sowTo ? 1 : 0)
    + (filters.showSnoozed ? 1 : 0);
}

function uniquePeople(people: Array<{ key: string | null; name: string | null }>): Array<{ key: string; name: string }> {
  const byKey = new Map<string, string>();
  for (const person of people) {
    if (!person.key || !person.name) continue;
    byKey.set(person.key, person.name);
  }
  return [...byKey.entries()]
    .map(([key, name]) => ({ key, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function fmtHours(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function DigestRunsPanel(): JSX.Element {
  const qc = useQueryClient();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const configQuery = useQuery({ queryKey: ['engagement-digest-config'], queryFn: getEngagementDigestConfig });
  const runsQuery = useQuery({ queryKey: ['engagement-digest-runs'], queryFn: getEngagementDigestRuns });
  const runs = runsQuery.data ?? [];

  useEffect(() => {
    if (!selectedRunId && runs[0]?.id) setSelectedRunId(runs[0].id);
  }, [runs, selectedRunId]);

  const detailQuery = useQuery({
    queryKey: ['engagement-digest-run', selectedRunId],
    queryFn: () => getEngagementDigestRun(selectedRunId!),
    enabled: !!selectedRunId && selectedRunId !== 'mock-digest-run',
  });

  const generate = useMutation({
    mutationFn: generateEngagementDigestPreview,
    onSuccess: async ({ runId }) => {
      setSelectedRunId(runId);
      await qc.invalidateQueries({ queryKey: ['engagement-digest-runs'] });
      await qc.invalidateQueries({ queryKey: ['engagement-digest-run', runId] });
    },
  });
  const deliver = useMutation({
    mutationFn: ({ mode, retry }: { mode: 'test' | 'live'; retry?: boolean }) =>
      sendEngagementDigestRun(selectedRunId!, mode, retry),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['engagement-digest-runs'] });
      await qc.invalidateQueries({ queryKey: ['engagement-digest-run', selectedRunId] });
    },
  });

  const runtime = configQuery.data;
  const detail = detailQuery.data;
  const selectedRun = detail?.run ?? runs.find((run) => run.id === selectedRunId) ?? null;
  const error = configQuery.error ?? runsQuery.error ?? detailQuery.error ?? generate.error ?? deliver.error;
  const nextRunLabel = runtime
    ? formatDigestNextRun(runtime.nextScheduledRunAt, runtime.timezone)
    : null;

  const requestDelivery = (mode: 'test' | 'live', retry = false) => {
    if (!selectedRunId) return;
    const destination = mode === 'test' ? runtime?.testRecipient : 'the intended Owners and CSMs';
    const action = retry ? 'Retry failed deliveries' : mode === 'test' ? 'Send every preview as a test copy' : 'Send this run live';
    if (!window.confirm(`${action} to ${destination ?? 'the configured recipient'}?`)) return;
    deliver.mutate({ mode, retry });
  };

  return (
    <div className="digest-panel">
      <div className="digest-panel-hd">
        <div>
          <h2>AM/CSM digest runs</h2>
          <p>Every preview refreshes Salesforce first. Live and scheduled delivery remain separately gated.</p>
        </div>
        <Button variant="primary" icon="refresh" onClick={() => generate.mutate()} disabled={generate.isPending}>
          {generate.isPending ? 'Refreshing & generating…' : 'Generate fresh preview'}
        </Button>
      </div>

      {runtime && (
        <div className="digest-runtime">
          <span className={`pill ${runtime.deliveryMode === 'live' ? 'running' : runtime.deliveryMode === 'test' ? 'active' : 'cancelled'}`}>
            Delivery {runtime.deliveryMode}
          </span>
          <span>
            Schedule {runtime.cronEnabled ? 'on' : 'off'} · {formatDigestSchedule(runtime.cron, runtime.timezone)}
          </span>
          {runtime.cronEnabled && nextRunLabel && <span>Next run {nextRunLabel}</span>}
          <span>Excluded from delivery: {runtime.excludedAccountRollups.join(', ') || 'none'}</span>
          <span>Sender: {runtime.sender}</span>
        </div>
      )}

      {error && (
        <div className="digest-error">
          <Icon name="alert" size={14} /> {error instanceof Error ? error.message : 'Could not load digest controls'}
        </div>
      )}

      <div className="digest-layout">
        <div className="digest-runs-list">
          <div className="digest-section-label">Recent runs</div>
          {runsQuery.isLoading ? <div className="d-meta">Loading runs…</div> : runs.length === 0 ? (
            <div className="d-meta">No digest runs yet.</div>
          ) : runs.map((run) => (
            <button
              type="button"
              key={run.id}
              className={`digest-run-card${run.id === selectedRunId ? ' selected' : ''}`}
              onClick={() => setSelectedRunId(run.id)}
            >
              <span><strong>{formatDigestRunLabel(run.period_key)}</strong> · {run.trigger}</span>
              <span className={`pill ${run.status}`}>{run.status}</span>
              <span className="d-meta">{run.recipients_count} recipients · {run.engagements_count} engagements</span>
              <span className="d-meta">{fmt.dateTime(run.generated_at)}</span>
            </button>
          ))}
        </div>

        <div className="digest-run-detail">
          {!selectedRun ? (
            <EmptyState icon="user" title="Generate the first preview" body="No email is sent during generation." />
          ) : (
            <>
              <div className="digest-detail-hd">
                <div>
                  <div className="digest-section-label">{formatDigestRunLabel(selectedRun.period_key)}</div>
                  <div className="digest-counts">
                    <strong>{selectedRun.recipients_count}</strong> recipients · <strong>{selectedRun.engagements_count}</strong> actionable · <strong>{selectedRun.excluded_count}</strong> excluded · <strong>{selectedRun.blocked_count}</strong> blocked · <strong>{selectedRun.failed_count}</strong> failed
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Button size="sm" variant="secondary" onClick={() => requestDelivery('test')} disabled={!runtime?.testSendEnabled || deliver.isPending} title={runtime?.testSendEnabled ? `Routes all copies to ${runtime.testRecipient}` : 'Enable ENGAGEMENT_DIGEST_TEST_SEND_ENABLED first'}>
                    Send test cycle
                  </Button>
                  <Button size="sm" variant="primary" onClick={() => requestDelivery('live')} disabled={!runtime?.liveSendEnabled || deliver.isPending} title={runtime?.liveSendEnabled ? 'Send to intended recipients' : 'Live sending is disabled'}>
                    Send live
                  </Button>
                  {(selectedRun.failed_count > 0 || selectedRun.status === 'failed') && (
                    <Button size="sm" variant="secondary" onClick={() => requestDelivery(runtime?.liveSendEnabled ? 'live' : 'test', true)} disabled={deliver.isPending}>
                      Retry failed
                    </Button>
                  )}
                </div>
              </div>
              {selectedRun.error_message && <div className="digest-error">{selectedRun.error_message}</div>}
              {detailQuery.isLoading ? <div className="d-meta" style={{ padding: 20 }}>Loading recipient previews…</div> : (
                <div className="digest-messages">
                  {(detail?.messages ?? []).length === 0 ? (
                    <EmptyState icon="check" title="No actionable recipients" body="No email will be sent for this run." />
                  ) : (detail?.messages ?? []).map((message) => <DigestMessagePreview key={message.id} message={message} />)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DigestMessagePreview({ message }: { message: EngagementDigestMessage }): JSX.Element {
  return (
    <details className="digest-message" open={message.status === 'failed' || message.status === 'blocked'}>
      <summary>
        <span>
          <strong>{message.recipient_name ?? 'Recipient not resolved'}</strong>
          <span className="d-meta">{message.intended_recipient_email ?? message.blocked_reason ?? 'Missing email'}</span>
        </span>
        <span className="d-meta">{message.engagement_ids.length} engagement{message.engagement_ids.length === 1 ? '' : 's'}</span>
        <span className={`pill ${message.status === 'preview' ? 'active' : message.status === 'blocked' || message.status === 'failed' ? 'failed' : 'succeeded'}`}>{message.status.replace('_', ' ')}</span>
      </summary>
      <div className="digest-message-body">
        <div><strong>{message.subject}</strong></div>
        {message.error_message && <div className="digest-error">{message.error_message}</div>}
        <pre>{message.body_text}</pre>
        <a className="btn-link" href={message.dashboard_url} target="_blank" rel="noopener noreferrer">Open recipient dashboard view</a>
      </div>
    </details>
  );
}

const RISK_TIER_STYLES: Record<RiskTier, { background: string; color: string; bar: string }> = {
  critical: { background: 'rgba(180, 35, 24, 0.12)', color: '#9f2018', bar: 'var(--risk)' },
  at_risk: { background: 'rgba(184, 67, 10, 0.12)', color: '#9a3c08', bar: 'var(--warn)' },
  watch: { background: 'rgba(185, 138, 0, 0.14)', color: '#765700', bar: '#B98A00' },
  healthy: { background: 'rgba(20, 122, 74, 0.12)', color: '#126a41', bar: 'var(--ok)' },
  not_scored: { background: 'var(--surface-2)', color: 'var(--mute)', bar: 'transparent' },
};

function RiskRankingTable({ engagements }: { engagements: EngagementAssessment[] }): JSX.Element {
  return (
    <table className="tbl gen health-table">
      <thead>
        <tr>
          <th>Risk</th>
          <th>Signals</th>
          <th>Reporting account</th>
          <th>Engagement / project</th>
          <th>Owner / CSM</th>
          <th>Status / affiliate</th>
          <th>Utilization</th>
          <th>SOW / activity</th>
          <th style={{ width: 1 }} />
        </tr>
      </thead>
      <tbody>
        {engagements.map((engagement) => (
          <RiskRankingRow key={engagement.opportunityId} engagement={engagement} />
        ))}
      </tbody>
    </table>
  );
}

function RiskRankingRow({ engagement }: { engagement: EngagementAssessment }): JSX.Element {
  const tierStyle = RISK_TIER_STYLES[engagement.riskTier];
  const primaryAffiliate = engagement.deliveringAffiliates[0];
  const extraAffiliates = engagement.deliveringAffiliates.length - 1;
  const sfHref = SF_INSTANCE_URL
    ? `${SF_INSTANCE_URL.replace(/\/$/, '')}/${engagement.opportunityId}`
    : `https://login.salesforce.com/${engagement.opportunityId}`;
  const reportingAccount = accountNameForDisplay(engagement);
  const rawAccountDiffers = engagement.accountName && engagement.accountName !== reportingAccount;
  const remaining = engagement.contractedHours == null
    ? null
    : engagement.contractedHours - engagement.loggedHours;

  return (
    <tr>
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <strong className="d-tnum" style={{ fontSize: 17, minWidth: 26 }}>
              {engagement.riskScore ?? '—'}
            </strong>
            <span
              style={{
                background: tierStyle.background,
                color: tierStyle.color,
                borderRadius: 99,
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 7px',
                whiteSpace: 'nowrap',
              }}
            >
              {RISK_TIER_LABELS[engagement.riskTier]}
            </span>
          </div>
          {engagement.riskScore != null && (
            <div
              style={{
                height: 3,
                width: 88,
                background: 'rgba(10,21,40,0.08)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, engagement.riskScore)}%`,
                  height: '100%',
                  background: tierStyle.bar,
                }}
              />
            </div>
          )}
        </div>
      </td>
      <td>
        {engagement.riskSignals.length > 0 ? (
          <div className="sig-list">
            {engagement.riskSignals.map((signal) => (
              <SignalChip key={signal.type} type={signal.type} points={signal.points} />
            ))}
          </div>
        ) : <span className="em">—</span>}
      </td>
      <td>
        <div>{reportingAccount ?? <span className="em">—</span>}</div>
        {rawAccountDiffers && <div className="d-meta health-sub">{engagement.accountName}</div>}
      </td>
      <td>
        <div>{engagement.opportunityName ?? <span className="em">—</span>}</div>
        <div className="d-meta health-sub">
          {[engagement.coacheeName, engagement.projectNumbers.join(', ')].filter(Boolean).join(' · ') || '—'}
        </div>
      </td>
      <td>
        <div>{engagement.ownerName ?? <span className="em">—</span>}</div>
        <div className="d-meta health-sub">CSM: {engagement.csmName ?? '—'}</div>
      </td>
      <td>
        <div>{engagement.engagementStatus?.trim() || <span className="em">—</span>}</div>
        <div className="d-meta health-sub">
          {primaryAffiliate?.name ?? 'No affiliate'}{extraAffiliates > 0 ? ` +${extraAffiliates}` : ''}
        </div>
      </td>
      <td>
        <HoursBar
          logged={engagement.loggedHours}
          contracted={engagement.contractedHours}
          pct={engagement.pctComplete}
        />
        <div className="d-meta health-sub">
          {remaining == null ? 'Remaining unavailable' : `${fmtHours(remaining)} h remaining`}
        </div>
      </td>
      <td>
        <div className="d-tnum">End {engagement.sowEndDate ? fmt.date(engagement.sowEndDate) : '—'}</div>
        <div className="d-meta health-sub d-tnum">Last {engagement.lastSessionDate ? fmt.date(engagement.lastSessionDate) : '—'}</div>
      </td>
      <td style={{ paddingRight: 16 }}>
        <a
          className="btn btn-sm btn-ghost"
          href={sfHref}
          target="_blank"
          rel="noopener noreferrer"
          title="Open in Salesforce"
        >
          <Icon name="link" size={13} />
          SF
        </a>
      </td>
    </tr>
  );
}

interface RowProps {
  a: AlertRow;
  onSnooze(): void;
  onUnsnooze(): void;
}

function AlertRowView({ a, onSnooze, onUnsnooze }: RowProps): JSX.Element {
  const now = Date.now();
  const snoozed = a.snooze && Date.parse(a.snooze.snoozedUntil) > now;
  const sfHref = SF_INSTANCE_URL
    ? `${SF_INSTANCE_URL.replace(/\/$/, '')}/${a.opportunityId}`
    : `https://login.salesforce.com/${a.opportunityId}`;

  const mailtoHref = buildAlertMailto(a);
  const primaryAffiliate = a.deliveringAffiliates?.[0];
  const extraAffiliates = (a.deliveringAffiliates?.length ?? 0) - 1;
  const reportingAccount = a.accountDisplayName?.trim() || a.accountName?.trim() || null;
  const remaining = a.contractedHours == null ? null : a.contractedHours - a.loggedHours;

  return (
    <tr>
      <td><AlertTypePill type={a.alertType} /></td>
      <td>
        <div>{reportingAccount ?? <span className="em">—</span>}</div>
        {a.accountName && a.accountName !== reportingAccount && <div className="d-meta health-sub">{a.accountName}</div>}
      </td>
      <td>
        <div>{a.opportunityName ?? <span className="em">—</span>}</div>
        <div className="d-meta health-sub">{[a.coacheeName, a.projectNumbers.join(', ')].filter(Boolean).join(' · ') || '—'}</div>
      </td>
      <td>
        <div>{a.ownerName ?? <span className="em">—</span>}</div>
        <div className="d-meta health-sub">CSM: {a.csmName ?? '—'}</div>
      </td>
      <td>
        <div>{a.engagementStatus?.trim() || <span className="em">—</span>}</div>
        <div className="d-meta health-sub">{primaryAffiliate?.name ?? 'No affiliate'}{extraAffiliates > 0 ? ` +${extraAffiliates}` : ''}</div>
      </td>
      <td>
        <HoursBar
          logged={a.loggedHours}
          contracted={a.contractedHours}
          pct={a.pctComplete}
        />
        <div className="d-meta health-sub">{remaining == null ? 'Remaining unavailable' : `${fmtHours(remaining)} h remaining`}</div>
      </td>
      <td>
        <div className="d-tnum">End {a.sowEndDate ? fmt.date(a.sowEndDate) : '—'}</div>
        <div className="d-meta health-sub d-tnum">Last {a.lastSessionDate ? fmt.date(a.lastSessionDate) : '—'}</div>
      </td>
      <td style={{ paddingRight: 16 }}>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
          <a
            className="btn btn-sm btn-ghost"
            href={sfHref}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in Salesforce"
            onClick={(e) => e.stopPropagation()}
          >
            <Icon name="link" size={13} />
            SF
          </a>
          {mailtoHref && (
            <a
              className="btn btn-sm btn-ghost"
              href={mailtoHref}
              title={`Draft email to ${primaryAffiliate?.name ?? 'affiliate'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Icon name="user" size={13} />
              Email
            </a>
          )}
          {snoozed ? (
            <Button size="sm" variant="ghost" onClick={onUnsnooze} title="Un-snooze">
              <Icon name="check" size={13} />
              Snoozed · {fmt.date(a.snooze!.snoozedUntil)}
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={onSnooze} title="Snooze 7 days">
              <Icon name="calendar" size={13} />
              Snooze 7d
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
